import type { Locale } from "./locale";
import { DRAWING_FACTS } from "../data/drawingFacts";
import { WHY_REASONS, WHY_PARENT_POINTS } from "../data/whyDrawing";

// The card decks behind the sub-pages (Fun Facts, Why Draw, Privacy, Rules,
// Say hello). The English decks for Facts and Why Draw live in `src/data/` so
// the prerendered SEO pages can share them; the Privacy/Rules/Contact decks are
// app-only and live here. Each locale supplies its own deck — a card list has
// no reusable structure to overlay, unlike a drawing's geometry.

export interface Card {
  emoji: string;
  text: string;
}
export interface ParentPoint {
  title: string;
  text: string;
}

// --- English (app-only decks; Facts + Why Draw are imported above) ----------

const PRIVACY_EN: Card[] = [
  { emoji: "🍪", text: "No cookies here — only the kind you eat are yummy! We don't use any." },
  { emoji: "💾", text: "Your drawings and stars stay right here in your own device — they never go anywhere else." },
  { emoji: "🙈", text: "No ads, no accounts, and no trackers following you around — we only count how many people visit, with no names and no cookies." },
  { emoji: "💌", text: "If a grown-up sends us feedback, only what they type in the form is emailed to us — nothing else." },
  { emoji: "🔒", text: "The grown-up code, timer and full-screen settings live only on this device." },
  { emoji: "🎤", text: 'If you turn on "Talk to me", your browser listens to help — turn it off any time!' },
  { emoji: "💛", text: "We made this just for fun, to help you become a happy little artist." },
];

const TERMS_EN: Card[] = [
  { emoji: "🎉", text: "Learn 2 Draw is free to use and just for fun — draw as much as you like!" },
  { emoji: "🖍️", text: "Everything you make here is yours. Be proud and keep creating!" },
  { emoji: "🤝", text: "Please be kind and use the app the nice way it was meant to be used." },
  { emoji: "🎨", text: "The drawings and pictures in the app belong to us — enjoy them, but please don't copy them to sell." },
  { emoji: "🌈", text: "We try to keep everything working and safe, but it's a playful app — have fun and don't worry!" },
];

const CONTACT_EN: Card[] = [
  { emoji: "👋", text: "Hello there, and thank you for visiting!" },
  { emoji: "🎨", text: "We hope you had heaps of fun drawing today." },
  { emoji: "💌", text: "This little app was made with love for young artists everywhere." },
  { emoji: "🌟", text: "Keep being creative — there's no wrong way to draw!" },
];

// --- French -----------------------------------------------------------------

const FACTS_FR: Card[] = [
  { emoji: "🦣", text: "Les plus vieux dessins ont plus de 40 000 ans — ils sont dans des grottes !" },
  { emoji: "🖐️", text: "Léonard de Vinci savait dessiner d'une main et écrire de l'autre en même temps !" },
  { emoji: "🧠", text: "Gribouiller aide ton cerveau à mieux se souvenir des choses." },
  { emoji: "🖍️", text: "La première boîte de crayons de couleur a été vendue il y a plus de 100 ans." },
  { emoji: "🎨", text: "Il n'y a pas de mauvaise façon de dessiner — chaque artiste a son propre style !" },
  { emoji: "✏️", text: "Un seul crayon peut tracer un trait de plus de 50 kilomètres !" },
  { emoji: "🟣", text: "Mélange du rouge et du bleu et tu obtiens du violet. Essaie !" },
  { emoji: "🚀", text: "Des astronautes ont déjà dessiné dans l'espace !" },
  { emoji: "🌈", text: "Un arc-en-ciel a 7 couleurs — utilise tous tes crayons !" },
  { emoji: "🐙", text: "La pieuvre peut changer de couleur, comme si elle se dessinait dessus." },
  { emoji: "👶", text: "Les bébés adorent gribouiller — c'est comme ça qu'ils apprennent à dessiner !" },
  { emoji: "🖌️", text: "Les premiers pinceaux étaient faits de poils d'animaux et de brindilles." },
  { emoji: "🔵", text: "Mélange du bleu et du jaune et tu obtiens du vert — comme par magie !" },
  { emoji: "🐘", text: "Certains éléphants savent tenir un pinceau avec leur trompe et peindre !" },
  { emoji: "📏", text: "Dessiner des formes comme des cercles et des carrés t'aide à tout dessiner." },
  { emoji: "🌙", text: "Des artistes ont dessiné des cartes de la Lune bien avant qu'on y aille." },
  { emoji: "🐝", text: "Les abeilles voient des couleurs invisibles pour nous — elles dessineraient avec des crayons secrets !" },
  { emoji: "👀", text: "Bien regarder les choses t'aide à mieux les dessiner." },
  { emoji: "🦖", text: "On sait à quoi ressemblaient les dinosaures grâce aux dessins des artistes." },
  { emoji: "❤️", text: "Dessiner pour quelqu'un qu'on aime, c'est le plus beau des cadeaux." },
];

const WHY_REASONS_FR: Card[] = [
  { emoji: "✏️", text: "Dessiner rend tes mains fortes et prêtes à écrire !" },
  { emoji: "💬", text: "Quand les mots sont difficiles, tu peux montrer ce que tu ressens avec un dessin." },
  { emoji: "🧠", text: "Transformer les choses réelles en dessins fait grandir ton cerveau." },
  { emoji: "🧩", text: "Si un dessin part de travers, tu apprends à le réparer et à réessayer." },
  { emoji: "🌟", text: "Finir un dessin te rend fier et tout content !" },
  { emoji: "🌈", text: "Il n'y a pas de mauvaise façon de dessiner — c'est ton imagination qui commande !" },
  { emoji: "😌", text: "Colorier et dessiner, c'est calme, douillet et reposant." },
  { emoji: "👨‍👩‍👧", text: "Dessiner avec quelqu'un qu'on aime, c'est le plus chouette des moments." },
];

const WHY_PARENT_POINTS_FR: ParentPoint[] = [
  {
    title: "Développe la motricité fine.",
    text: "Tenir des crayons aide l'enfant à affiner sa prise et le contrôle de son poignet — la coordination qui prépare les premiers gestes de l'écriture.",
  },
  {
    title: "Favorise la communication.",
    text: "Les enfants se représentent des idées et des émotions bien avant de savoir les dire. Le dessin leur donne un moyen d'exprimer ce qu'ils ne peuvent pas encore formuler.",
  },
  {
    title: "Soutient la réflexion.",
    text: "Traduire un monde en trois dimensions sur une feuille plate développe le sens de l'espace, des proportions et de la planification, et fait travailler la mémoire comme la résolution de problèmes.",
  },
  {
    title: "Apprend la patience et la débrouillardise.",
    text: "Quand un dessin ne ressemble pas à ce qui était prévu, l'enfant apprend à s'adapter, à corriger et à essayer autrement — un entraînement discret et répété à la persévérance.",
  },
  {
    title: "Éveille la créativité.",
    text: "Une page blanche n'a pas de bonne réponse unique : l'imagination mène la danse et il reste de la place pour inventer.",
  },
  {
    title: "Apaise et rapproche.",
    text: "Le dessin est naturellement apaisant et aide à traverser les grandes émotions — et dessiner à côté d'un adulte ou d'un frère ou d'une sœur, c'est un joli moment ensemble, loin des écrans.",
  },
  {
    title: "Renforce la confiance.",
    text: "Terminer un dessin procure un vrai sentiment d'accomplissement et la fierté du « c'est moi qui l'ai fait ».",
  },
];

const PRIVACY_FR: Card[] = [
  { emoji: "🍪", text: "Pas de cookies ici — les seuls bons cookies, c'est ceux qu'on mange ! On n'en utilise aucun." },
  { emoji: "💾", text: "Tes dessins et tes étoiles restent sur ton appareil — ils ne partent jamais ailleurs." },
  { emoji: "🙈", text: "Pas de pubs, pas de comptes, et personne qui te suit — on compte seulement combien de personnes visitent le site, sans noms et sans cookies." },
  { emoji: "💌", text: "Si un adulte nous envoie un message, on ne reçoit que ce qu'il écrit dans le formulaire — rien d'autre." },
  { emoji: "🔒", text: "Le code des grands, le minuteur et le mode plein écran restent uniquement sur cet appareil." },
  { emoji: "🎤", text: "Si tu actives « Parle-moi », ton navigateur écoute pour t'aider — tu peux l'éteindre quand tu veux !" },
  { emoji: "💛", text: "On a fait ce site juste pour le plaisir, pour t'aider à devenir un joyeux petit artiste." },
];

const TERMS_FR: Card[] = [
  { emoji: "🎉", text: "Learn 2 Draw est gratuit et fait pour s'amuser — dessine autant que tu veux !" },
  { emoji: "🖍️", text: "Tout ce que tu crées ici est à toi. Sois fier et continue de créer !" },
  { emoji: "🤝", text: "Sois gentil et utilise l'application comme elle a été pensée." },
  { emoji: "🎨", text: "Les dessins et les images de l'application nous appartiennent — profites-en, mais ne les copie pas pour les vendre." },
  { emoji: "🌈", text: "On essaie que tout marche bien et en sécurité, mais c'est une application pour jouer — amuse-toi et ne t'inquiète pas !" },
];

const CONTACT_FR: Card[] = [
  { emoji: "👋", text: "Bonjour, et merci de ta visite !" },
  { emoji: "🎨", text: "On espère que tu t'es bien amusé à dessiner aujourd'hui." },
  { emoji: "💌", text: "Cette petite application a été faite avec amour pour les jeunes artistes du monde entier." },
  { emoji: "🌟", text: "Continue d'être créatif — il n'y a pas de mauvaise façon de dessiner !" },
];

// --- Greek -------------------------------------------------------------------

const FACTS_EL: Card[] = [
  { emoji: "🦣", text: "Τα πιο παλιά σχέδια είναι πάνω από 40.000 χρόνων — βρίσκονται σε σπηλιές!" },
  { emoji: "🖐️", text: "Ο Λεονάρντο ντα Βίντσι μπορούσε να ζωγραφίζει με το ένα χέρι και να γράφει με το άλλο ταυτόχρονα!" },
  { emoji: "🧠", text: "Το μουτζούρωμα βοηθά το μυαλό σου να θυμάται καλύτερα τα πράγματα." },
  { emoji: "🖍️", text: "Το πρώτο κουτί με ξυλομπογιές πουλήθηκε πριν από πάνω από 100 χρόνια." },
  { emoji: "🎨", text: "Δεν υπάρχει λάθος τρόπος να ζωγραφίζεις — κάθε καλλιτέχνης έχει το δικό του στιλ!" },
  { emoji: "✏️", text: "Ένα μόνο μολύβι μπορεί να τραβήξει μια γραμμή πάνω από 50 χιλιόμετρα!" },
  { emoji: "🟣", text: "Ανακάτεψε κόκκινο και μπλε και βγάζεις μοβ. Δοκίμασε!" },
  { emoji: "🚀", text: "Αστροναύτες έχουν ζωγραφίσει στο διάστημα!" },
  { emoji: "🌈", text: "Το ουράνιο τόξο έχει 7 χρώματα — χρησιμοποίησε όλες σου τις μπογιές!" },
  { emoji: "🐙", text: "Το χταπόδι μπορεί να αλλάζει χρώμα, σαν να ζωγραφίζει πάνω στον εαυτό του." },
  { emoji: "👶", text: "Τα μωρά λατρεύουν να μουτζουρώνουν — έτσι μαθαίνουν να ζωγραφίζουν!" },
  { emoji: "🖌️", text: "Τα πρώτα πινέλα ήταν φτιαγμένα από τρίχες ζώων και κλαδάκια." },
  { emoji: "🔵", text: "Ανακάτεψε μπλε και κίτρινο και βγάζεις πράσινο — σαν μαγικό!" },
  { emoji: "🐘", text: "Μερικοί ελέφαντες κρατούν πινέλο με την προβοσκίδα τους και ζωγραφίζουν!" },
  { emoji: "📏", text: "Το να ζωγραφίζεις σχήματα σαν κύκλους και τετράγωνα σε βοηθά να ζωγραφίζεις τα πάντα." },
  { emoji: "🌙", text: "Καλλιτέχνες ζωγράφισαν χάρτες της Σελήνης πολύ πριν πάμε εκεί." },
  { emoji: "🐝", text: "Οι μέλισσες βλέπουν χρώματα αόρατα για εμάς — θα ζωγράφιζαν με μυστικές μπογιές!" },
  { emoji: "👀", text: "Το να παρατηρείς καλά τα πράγματα σε βοηθά να τα ζωγραφίζεις καλύτερα." },
  { emoji: "🦖", text: "Ξέρουμε πώς έμοιαζαν οι δεινόσαυροι χάρη στα σχέδια των καλλιτεχνών." },
  { emoji: "❤️", text: "Το να ζωγραφίζεις για κάποιον που αγαπάς είναι το πιο όμορφο δώρο." },
];

const WHY_REASONS_EL: Card[] = [
  { emoji: "✏️", text: "Η ζωγραφική κάνει τα χέρια σου δυνατά και έτοιμα να γράψουν!" },
  { emoji: "💬", text: "Όταν οι λέξεις είναι δύσκολες, μπορείς να δείξεις πώς νιώθεις με ένα σχέδιο." },
  { emoji: "🧠", text: "Το να μετατρέπεις τα αληθινά πράγματα σε σχέδια μεγαλώνει το μυαλό σου." },
  { emoji: "🧩", text: "Αν ένα σχέδιο πάει στραβά, μαθαίνεις να το διορθώνεις και να ξαναπροσπαθείς." },
  { emoji: "🌟", text: "Όταν τελειώνεις ένα σχέδιο, νιώθεις περήφανος και χαρούμενος!" },
  { emoji: "🌈", text: "Δεν υπάρχει λάθος τρόπος να ζωγραφίζεις — η φαντασία σου κάνει κουμάντο!" },
  { emoji: "😌", text: "Το να χρωματίζεις και να ζωγραφίζεις είναι ήρεμο, ζεστό και ξεκούραστο." },
  { emoji: "👨‍👩‍👧", text: "Το να ζωγραφίζεις με κάποιον που αγαπάς είναι η πιο ωραία στιγμή." },
];

const WHY_PARENT_POINTS_EL: ParentPoint[] = [
  {
    title: "Αναπτύσσει τη λεπτή κινητικότητα.",
    text: "Το κράτημα των μολυβιών βοηθά το παιδί να τελειοποιήσει το πιάσιμο και τον έλεγχο του καρπού — τον συντονισμό που προετοιμάζει τις πρώτες κινήσεις της γραφής.",
  },
  {
    title: "Ενθαρρύνει την επικοινωνία.",
    text: "Τα παιδιά αναπαριστούν ιδέες και συναισθήματα πολύ πριν μπορέσουν να τα πουν. Η ζωγραφική τους δίνει έναν τρόπο να εκφράσουν αυτά που δεν μπορούν ακόμα να διατυπώσουν.",
  },
  {
    title: "Στηρίζει τη σκέψη.",
    text: "Το να αποδίδεις έναν τρισδιάστατο κόσμο σε ένα επίπεδο φύλλο αναπτύσσει την αίσθηση του χώρου, των αναλογιών και του σχεδιασμού, και δουλεύει τη μνήμη όσο και την επίλυση προβλημάτων.",
  },
  {
    title: "Μαθαίνει υπομονή και επινοητικότητα.",
    text: "Όταν ένα σχέδιο δεν μοιάζει με αυτό που σχεδιάστηκε, το παιδί μαθαίνει να προσαρμόζεται, να διορθώνει και να δοκιμάζει αλλιώς — μια διακριτική, επαναλαμβανόμενη εξάσκηση στην επιμονή.",
  },
  {
    title: "Ξυπνά τη δημιουργικότητα.",
    text: "Μια λευκή σελίδα δεν έχει μία μόνο σωστή απάντηση: η φαντασία οδηγεί τον χορό και μένει χώρος για εφευρετικότητα.",
  },
  {
    title: "Ηρεμεί και φέρνει κοντά.",
    text: "Η ζωγραφική είναι από τη φύση της καθησυχαστική και βοηθά να περνούν τα μεγάλα συναισθήματα — και το να ζωγραφίζεις δίπλα σε έναν μεγάλο ή ένα αδερφάκι είναι μια όμορφη στιγμή μαζί, μακριά από τις οθόνες.",
  },
  {
    title: "Ενισχύει την αυτοπεποίθηση.",
    text: "Το να τελειώνεις ένα σχέδιο δίνει μια αληθινή αίσθηση ολοκλήρωσης και την περηφάνια του «το έφτιαξα εγώ».",
  },
];

const PRIVACY_EL: Card[] = [
  { emoji: "🍪", text: "Χωρίς cookies εδώ — τα μόνα καλά cookies είναι αυτά που τρώμε! Δεν χρησιμοποιούμε κανένα." },
  { emoji: "💾", text: "Τα σχέδια και τα αστέρια σου μένουν στη δική σου συσκευή — δεν πάνε ποτέ πουθενά αλλού." },
  { emoji: "🙈", text: "Χωρίς διαφημίσεις, χωρίς λογαριασμούς και χωρίς ιχνηλάτες να σε ακολουθούν — μετράμε μόνο πόσοι επισκέπτονται, χωρίς ονόματα και χωρίς cookies." },
  { emoji: "💌", text: "Αν ένας μεγάλος μάς στείλει σχόλια, λαμβάνουμε μόνο ό,τι γράφει στη φόρμα — τίποτα άλλο." },
  { emoji: "🔒", text: "Ο κωδικός των μεγάλων, το χρονόμετρο και οι ρυθμίσεις πλήρους οθόνης μένουν μόνο σε αυτή τη συσκευή." },
  { emoji: "🎤", text: "Αν ανοίξεις το «Μίλα μου», ο περιηγητής σου ακούει για να βοηθήσει — κλείσ' το όποτε θέλεις!" },
  { emoji: "💛", text: "Το φτιάξαμε απλώς για διασκέδαση, για να σε βοηθήσουμε να γίνεις ένας χαρούμενος μικρός καλλιτέχνης." },
];

const TERMS_EL: Card[] = [
  { emoji: "🎉", text: "Το Learn 2 Draw είναι δωρεάν και μόνο για διασκέδαση — ζωγράφισε όσο θέλεις!" },
  { emoji: "🖍️", text: "Ό,τι φτιάχνεις εδώ είναι δικό σου. Να είσαι περήφανος και συνέχισε να δημιουργείς!" },
  { emoji: "🤝", text: "Να είσαι ευγενικός και να χρησιμοποιείς την εφαρμογή όπως σχεδιάστηκε." },
  { emoji: "🎨", text: "Τα σχέδια και οι εικόνες της εφαρμογής μάς ανήκουν — απόλαυσέ τα, αλλά μην τα αντιγράφεις για να τα πουλήσεις." },
  { emoji: "🌈", text: "Προσπαθούμε να λειτουργούν όλα σωστά και με ασφάλεια, αλλά είναι μια εφαρμογή για παιχνίδι — διασκέδασε και μην ανησυχείς!" },
];

const CONTACT_EL: Card[] = [
  { emoji: "👋", text: "Γεια σου, και ευχαριστούμε που μας επισκέφτηκες!" },
  { emoji: "🎨", text: "Ελπίζουμε να διασκέδασες πολύ ζωγραφίζοντας σήμερα." },
  { emoji: "💌", text: "Αυτή η μικρή εφαρμογή φτιάχτηκε με αγάπη για τους μικρούς καλλιτέχνες όλου του κόσμου." },
  { emoji: "🌟", text: "Συνέχισε να είσαι δημιουργικός — δεν υπάρχει λάθος τρόπος να ζωγραφίζεις!" },
];

// --- Dutch -------------------------------------------------------------------

const FACTS_NL: Card[] = [
  { emoji: "🦣", text: "De oudste tekeningen zijn meer dan 40.000 jaar oud — ze zitten in grotten!" },
  { emoji: "🖐️", text: "Leonardo da Vinci kon met de ene hand tekenen en met de andere tegelijk schrijven!" },
  { emoji: "🧠", text: "Krabbelen helpt je brein om dingen beter te onthouden." },
  { emoji: "🖍️", text: "De eerste doos kleurpotloden werd meer dan 100 jaar geleden verkocht." },
  { emoji: "🎨", text: "Er is geen verkeerde manier om te tekenen — elke kunstenaar heeft zijn eigen stijl!" },
  { emoji: "✏️", text: "Eén potlood kan een lijn van meer dan 50 kilometer trekken!" },
  { emoji: "🟣", text: "Meng rood en blauw en je krijgt paars. Probeer maar!" },
  { emoji: "🚀", text: "Astronauten hebben in de ruimte getekend!" },
  { emoji: "🌈", text: "Een regenboog heeft 7 kleuren — gebruik al je potloden!" },
  { emoji: "🐙", text: "De octopus kan van kleur veranderen, alsof hij op zichzelf tekent." },
  { emoji: "👶", text: "Baby's houden van krabbelen — zo leren ze tekenen!" },
  { emoji: "🖌️", text: "De eerste penselen waren gemaakt van dierenharen en takjes." },
  { emoji: "🔵", text: "Meng blauw en geel en je krijgt groen — net magie!" },
  { emoji: "🐘", text: "Sommige olifanten kunnen een penseel met hun slurf vasthouden en schilderen!" },
  { emoji: "📏", text: "Vormen tekenen zoals cirkels en vierkanten helpt je om alles te tekenen." },
  { emoji: "🌙", text: "Kunstenaars tekenden al kaarten van de maan voordat we erheen gingen." },
  { emoji: "🐝", text: "Bijen zien kleuren die wij niet kunnen zien — zij zouden met geheime potloden tekenen!" },
  { emoji: "👀", text: "Goed naar dingen kijken helpt je om ze beter te tekenen." },
  { emoji: "🦖", text: "We weten hoe dinosauriërs eruitzagen dankzij tekeningen van kunstenaars." },
  { emoji: "❤️", text: "Tekenen voor iemand van wie je houdt, is het mooiste cadeau." },
];

const WHY_REASONS_NL: Card[] = [
  { emoji: "✏️", text: "Tekenen maakt je handen sterk en klaar om te schrijven!" },
  { emoji: "💬", text: "Als woorden moeilijk zijn, kun je laten zien hoe je je voelt met een tekening." },
  { emoji: "🧠", text: "Echte dingen omzetten in tekeningen laat je brein groeien." },
  { emoji: "🧩", text: "Als een tekening mislukt, leer je hem te repareren en opnieuw te proberen." },
  { emoji: "🌟", text: "Een tekening afmaken maakt je trots en blij!" },
  { emoji: "🌈", text: "Er is geen verkeerde manier om te tekenen — jouw fantasie bepaalt!" },
  { emoji: "😌", text: "Kleuren en tekenen is rustig, knus en ontspannend." },
  { emoji: "👨‍👩‍👧", text: "Samen tekenen met iemand van wie je houdt, is het allerleukste." },
];

const WHY_PARENT_POINTS_NL: ParentPoint[] = [
  {
    title: "Ontwikkelt de fijne motoriek.",
    text: "Het vasthouden van potloden helpt het kind om zijn grip en polscontrole te verfijnen — de coördinatie die de eerste schrijfbewegingen voorbereidt.",
  },
  {
    title: "Bevordert communicatie.",
    text: "Kinderen verbeelden ideeën en emoties lang voordat ze ze onder woorden kunnen brengen. Tekenen geeft hen een manier om uit te drukken wat ze nog niet kunnen formuleren.",
  },
  {
    title: "Ondersteunt het denken.",
    text: "Het vertalen van een driedimensionale wereld naar een plat vel papier ontwikkelt ruimtelijk inzicht, verhoudingen en planning, en traint zowel het geheugen als het probleemoplossend vermogen.",
  },
  {
    title: "Leert geduld en vindingrijkheid.",
    text: "Wanneer een tekening niet lijkt op wat bedoeld was, leert het kind zich aan te passen, te corrigeren en anders te proberen — een stille, herhaalde oefening in volharding.",
  },
  {
    title: "Prikt de creativiteit aan.",
    text: "Een leeg vel papier heeft niet één goed antwoord: de verbeelding bepaalt de koers en er is ruimte om te verzinnen.",
  },
  {
    title: "Stilt en brengt samen.",
    text: "Tekenen is van nature rustgevend en helpt bij grote emoties — en tekenen naast een volwassene of een broertje of zusje is een mooi moment samen, weg van schermen.",
  },
  {
    title: "Versterkt het zelfvertrouwen.",
    text: "Het afmaken van een tekening geeft een echt gevoel van voldoening en de trots van 'ik heb het zelf gedaan'.",
  },
];

const PRIVACY_NL: Card[] = [
  { emoji: "🍪", text: "Geen cookies hier — de enige lekkere cookies zijn die je eet! Wij gebruiken ze niet." },
  { emoji: "💾", text: "Jouw tekeningen en sterren blijven op je eigen apparaat — ze gaan nooit ergens anders heen." },
  { emoji: "🙈", text: "Geen advertenties, geen accounts en geen trackers die je volgen — we tellen alleen hoeveel mensen de site bezoeken, zonder namen en zonder cookies." },
  { emoji: "💌", text: "Als een volwassene ons feedback stuurt, ontvangen we alleen wat er in het formulier staat — niets anders." },
  { emoji: "🔒", text: "De volwassenencode, timer en volledig-scherm-instellingen blijven alleen op dit apparaat." },
  { emoji: "🎤", text: 'Als je "Praat tegen me" aanzet, luistert je browser om te helpen — je kunt het altijd uitzetten!' },
  { emoji: "💛", text: "We hebben dit gemaakt voor de lol, om jou te helpen een blije kleine kunstenaar te worden." },
];

const TERMS_NL: Card[] = [
  { emoji: "🎉", text: "Learn 2 Draw is gratis en alleen voor de lol — teken zoveel je wilt!" },
  { emoji: "🖍️", text: "Alles wat je hier maakt, is van jou. Wees trots en blijf creëren!" },
  { emoji: "🤝", text: "Wees lief en gebruik de app zoals hij bedoeld is." },
  { emoji: "🎨", text: "De tekeningen en plaatjes in de app zijn van ons — geniet ervan, maar kopieer ze niet om te verkopen." },
  { emoji: "🌈", text: "We proberen alles goed en veilig te laten werken, maar het is een speelse app — heb plezier en maak je geen zorgen!" },
];

const CONTACT_NL: Card[] = [
  { emoji: "👋", text: "Hallo daar, en bedankt voor je bezoek!" },
  { emoji: "🎨", text: "We hopen dat je heel veel plezier hebt gehad met tekenen vandaag." },
  { emoji: "💌", text: "Deze kleine app is gemaakt met liefde voor jonge kunstenaars over de hele wereld." },
  { emoji: "🌟", text: "Blijf creatief — er is geen verkeerde manier om te tekenen!" },
];

// --- per-locale decks -------------------------------------------------------

export interface Content {
  facts: Card[];
  whyReasons: Card[];
  whyParentPoints: ParentPoint[];
  privacyPoints: Card[];
  termsPoints: Card[];
  contactPoints: Card[];
}

export const CONTENT: Record<Locale, Content> = {
  en: {
    facts: DRAWING_FACTS,
    whyReasons: WHY_REASONS,
    whyParentPoints: WHY_PARENT_POINTS,
    privacyPoints: PRIVACY_EN,
    termsPoints: TERMS_EN,
    contactPoints: CONTACT_EN,
  },
  fr: {
    facts: FACTS_FR,
    whyReasons: WHY_REASONS_FR,
    whyParentPoints: WHY_PARENT_POINTS_FR,
    privacyPoints: PRIVACY_FR,
    termsPoints: TERMS_FR,
    contactPoints: CONTACT_FR,
  },
  el: {
    facts: FACTS_EL,
    whyReasons: WHY_REASONS_EL,
    whyParentPoints: WHY_PARENT_POINTS_EL,
    privacyPoints: PRIVACY_EL,
    termsPoints: TERMS_EL,
    contactPoints: CONTACT_EL,
  },
  nl: {
    facts: FACTS_NL,
    whyReasons: WHY_REASONS_NL,
    whyParentPoints: WHY_PARENT_POINTS_NL,
    privacyPoints: PRIVACY_NL,
    termsPoints: TERMS_NL,
    contactPoints: CONTACT_NL,
  },
};
